import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useHashPackWallet } from '../contexts/HashPackWalletContext';
import WalletStatus from './WalletStatus';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isConnected, accountId, disconnect } = useHashPackWallet();

  const formatAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
      navigate('/');
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const getActiveSection = () => {
    const path = location.pathname;
    if (path === '/chat') return 'chat';
    if (path === '/discover') return 'discover';
    if (path === '/tracking') return 'tracking';
    if (path === '/mygroup') return 'mygroup';
    if (path === '/orders') return 'orders';
    if (path === '/strategies') return 'strategies';
    if (path === '/orders/trading') return 'trading';
    if (path.startsWith('/lottery')) return 'lottery';
    if (path === '/profitable-swap') return 'profitable-swap';
    if (path === '/swap-lottery') return 'swap-lottery';
    return '';
  };

  const sidebarItems = [
    { id: 'chat', label: 'Copy Chat', icon: '🤖', path: '/chat' },
    { id: 'discover', label: 'Discover', icon: '🔍', path: '/discover' },
    { id: 'tracking', label: 'Token Tracking', icon: '🎯', path: '/tracking' },
    { id: 'profitable-swap', label: 'Profitable Swap', icon: '💰', path: '/profitable-swap' },
    { id: 'swap-lottery', label: 'Swap Lottery', icon: '🎲', path: '/swap-lottery' },
    { id: 'mygroup', label: 'My Groups', icon: '👥', path: '/mygroup' },
    { id: 'orders', label: 'Orders', icon: '📋', path: '/orders' },
    { id: 'strategies', label: 'Strategies', icon: '🎯', path: '/strategies' },
    { id: 'trading', label: 'Live Trading', icon: '📊', path: '/orders/trading' },
    { id: 'lottery', label: 'Lottery', icon: '🎰', path: '/lottery' },
  ];

  return (
    <div className="flex h-screen bg-black text-green-400 font-mono">
      {/* Sidebar */}
      <div className="w-64 bg-[#030306] border-r border-green-500/30 flex flex-col">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-green-500/30">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="COPYCAT Logo" 
              className="h-16 w-auto object-contain drop-shadow-[0_0_12px_rgba(34,197,94,0.7)] transition-transform duration-200 hover:scale-105"
              style={{ filter: 'drop-shadow(0 0 16px #22c55e88)' }}
            />
          </div>
          
          {/* Wallet Info */}
          {isConnected && (
            <div className="p-2 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded text-xs animate-pulse">
              <div className="text-green-400/70 mb-1 flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                Connected Wallet
              </div>
              <div className="text-green-300 font-medium">{formatAddress(accountId || '')}</div>
            </div>
          )}
        </div>

        {/* Sidebar Navigation */}
        <div className="flex-1 overflow-y-auto">
          <nav className="p-3">
            <div className="text-xs text-green-500/70 uppercase tracking-wide mb-3 px-3">
              Navigation
            </div>
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full text-left px-3 py-3 rounded-md text-sm transition-colors mb-1 flex items-center space-x-3 ${
                  getActiveSection() === item.id
                    ? 'bg-green-500/20 text-green-300 border-r-2 border-green-400'
                    : 'text-green-400/70 hover:bg-green-500/10 hover:text-green-300'
                }`}
              >
                <span className="text-base">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-green-500/30">
          <div className="space-y-3">
            <div className="text-xs text-green-500/70">Account Status</div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-green-400/70">Status:</span>
              <span className="text-green-400">Active</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-green-400/70">Session:</span>
              <span className="text-green-400">2h 34m</span>
            </div>
            <Button
              onClick={handleDisconnect}
              size="sm"
              variant="outline"
              className="w-full border-red-500/30 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 text-xs"
            >
              Disconnect
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Navigation */}
        <header className="bg-[#030306] border-b border-green-500/30 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-green-400">
                {sidebarItems.find(item => item.id === getActiveSection())?.label || 'COPYCAT'}
              </h1>
              <div className="text-green-500/70 text-sm">
                {new Date().toLocaleDateString()} • {new Date().toLocaleTimeString()}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-green-400/70 text-sm">
                <span className="text-green-400">●</span> System Online
              </div>
              <WalletStatus />
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>

        {/* Terminal Footer */}
        <footer className="bg-gradient-to-r from-[#0d0d2a] to-[#0a0a1a] border-t border-green-500/30 px-6 py-2">
          <div className="flex items-center justify-between text-xs text-green-500/70">
            <div className="flex items-center space-x-4">
              <span className="text-green-400">$ copycat --status</span>
              <span className="text-green-400 flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                Ready
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-green-400 flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                API: Connected
              </span>
              <span>Latency: 12ms</span>
              <span>Memory: 45.2MB</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AppLayout;
